<template>
  <VaCard>
    <VaCardContent class="flex-nowrap">
      <section class="flex flex-col gap-y-5 overflow-x-auto">
        <header class="flex flex-col gap-3 align-stretch">
          <h3 class="mb-3 font-bold text-lg">Embarcaciones activas por muelle</h3>
          <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
            <VaButtonToggle v-model="selectedMuelle" color="#EFEFEF" toggle-color="primary" :options="muelles" />
            <div
              class="p-1 rounded self-start sm:self-auto flex-shrink-0"
              :style="{
                backgroundColor: iconBackground,
                color: iconColor,
              }"
            >
              <slot name="icon"></slot>
            </div>
          </div>
        </header>
        <div class="body-content justify-between">
          <div class="scrollable-data-list">
            <div class="table-scroll-wrapper">
              <VaDataTable
                class="region-revenue-table"
                :columns="[
                  { key: 'Nave', label: 'Embarcación' },
                  { key: 'activeViajes', label: 'Viajes Activos', align: 'center' },
                  { key: 'Patrones', label: 'Patrones' },
                ]"
                :items="showList"
                :selectable="userRole!.role !== UserRoles.GESTOR"
                select-mode="multiple"
                selected-color="primary"
                @selectionChange="selectedItemsEmitted = $event.currentSelectedItems"
              >
                <template #cell(Nave)="{ rowData }">
                  {{ rowData.tipo_embarcacion == 'auxiliar' ? rowData.nave_madre : rowData.nombre_embarcacion }}
                </template>
                <template #cell(activeViajes)="{ rowData }">
                  {{ rowData.activeViajes.length }}
                </template>
                <template #cell(Patrones)="{ rowData }">
                  <div class="flex flex-col gap-1">
                    <div
                      v-for="viaje in rowData.activeViajes.reduce((uniquePat, viaje) => {
                        if (!uniquePat.some((v) => v.patronData.id === viaje.patronData.id)) {
                          uniquePat.push(viaje)
                        }
                        return uniquePat
                      }, [] as ViajePatron[])"
                      :key="viaje.id"
                      class="px-2 py-1 rounded"
                      :style="{ backgroundColor: getColor('primary-lighten-4'), color: 'white' }"
                    >
                      {{ viaje.patronData.nombre_completo }}
                    </div>
                  </div>
                </template>
              </VaDataTable>
            </div>
          </div>
          <VaButton
            v-if="
              showList.length > 0 &&
              (userRole!.role == UserRoles.ADMIN ||
                userRole!.role == UserRoles.SUPERVISOR ||
                userRole!.role == UserRoles.OPERADOR)
            "
            :disabled="selectedItemsEmitted.length == 0"
            color="danger"
            class="mt-2"
            preset="danger"
            @click="cancelViajes()"
          >
            Anular viajes de embarcaciones seleccionadas
          </VaButton>
        </div>
      </section>
    </VaCardContent>
  </VaCard>
</template>

<script lang="ts" setup>
import { computed, onMounted, Ref, ref, watch } from 'vue'
import { useColors, useToast } from 'vuestic-ui'
import { VaCard } from 'vuestic-ui'
import { NavesProperties } from '../../../data/NavesProperties'

import roleCapacitiesManager, {
  UserRoles,
  ExternalSpec,
  ArmadorType,
  UserRole,
  getRoleName,
} from '../../../managers/identity/RoleCapacitiesManager'
import { IEmbarcacion, OperatedEmbarcacion } from '../../../services/api-services/EmbarcacionesServices'
import dataStorageManager, { BucketDefaultIDs } from '../../../managers/data/DataStorageManager'
import { IRequest, RequestObjectType, RequestStatus } from '../../../services/api-services/RequestsServices'
import identityManager, { LoggedInUser } from '../../../managers/identity/IdentityManager'
import {
  IViaje,
  OperatedViaje,
  ViajeAprobado,
  ViajeEstado,
  ViajeMuelle,
  ViajeReservado,
  ViajeVencido,
} from '../../../services/api-services/ViajesServices'
import { ITripulante } from '../../../services/api-services/TripulantesServices'
import {
  defaultIIncidencia,
  IIncidencia,
  IncidenceCode,
  IncidenceType,
} from '../../../services/api-services/BitacoraServices'

interface ViajePatron extends IViaje {
  patronData: ITripulante
  preacreditaciones: IRequest[]
}

interface EmbarcacionViajes extends OperatedEmbarcacion {
  activeViajes: ViajePatron[]
}

const { getColor } = useColors()

const userRole: Ref<UserRole | null> = roleCapacitiesManager.getRefUserRole()

const muelles = [
  {
    label: 'Muelle Este',
    value: 0,
  },
  {
    label: 'Muelle Weste',
    value: 1,
  },
]
const selectedMuelle: Ref<number> = ref(0)

const embarcacionesList: Ref<IEmbarcacion[]> = ref([])
const fullEmbarcacionesList: Ref<EmbarcacionViajes[]> = ref([])
const showList: Ref<EmbarcacionViajes[]> = ref([])

const requestsList: Ref<IRequest[]> = ref([])
const asociationsRequests: Ref<IRequest[]> = ref([])
const preacreditacionRequests: Ref<IRequest[]> = ref([])

const posicionamientoRequests: Ref<IRequest[]> = ref([])
const viajesList: Ref<IViaje[]> = ref([])

const patronesRequests: Ref<IRequest[]> = ref([])
const patronesList: Ref<ITripulante[]> = ref([])

const selectedItemsEmitted = ref([])

const { init: notify } = useToast()

const iconBackground = getColor('warning')
const iconColor = getColor('on-warning')

const dataList: Ref<
  Array<{
    muelle: string
    naves: EmbarcacionViajes[]
  }>
> = ref([])

onMounted(() => {
  dataStorageManager.subscribeBucket<IEmbarcacion>(BucketDefaultIDs.EMBARCACIONES, (embarcaciones) => {
    embarcacionesList.value = embarcaciones
    loadFullEmbarcaciones()
  })
  dataStorageManager.subscribeBucket<IViaje>(BucketDefaultIDs.VIAJES, (viajes) => {
    onViajesLoad(viajes)
  })
  dataStorageManager.subscribeBucket<ITripulante>(BucketDefaultIDs.TRIPULANTES, (tripulantes) => {
    onTripulantesLoad(tripulantes)
  })
  dataStorageManager.subscribeBucket<IRequest>(BucketDefaultIDs.SOLICITUDES, (requests) => {
    onRequestsLoad(requests)
  })
})

watch(
  embarcacionesList,
  () => {
    loadFullEmbarcaciones()
  },
  { deep: true },
)

watch(fullEmbarcacionesList, () => {
  const embarcacionesMuelleEste: EmbarcacionViajes[] = fullEmbarcacionesList.value.filter((e) =>
    e.activeViajes.some((v) => v.muelle == ViajeMuelle.ESTE),
  )
  const embarcacionesMuelleWeste: EmbarcacionViajes[] = fullEmbarcacionesList.value.filter((e) =>
    e.activeViajes.some((v) => v.muelle == ViajeMuelle.WESTE),
  )

  dataList.value = [
    {
      muelle: 'Muelle Este',
      naves: embarcacionesMuelleEste,
    },
    {
      muelle: 'Muelle Weste',
      naves: embarcacionesMuelleWeste,
    },
  ]
})

watch([selectedMuelle, dataList], () => {
  showList.value = dataList.value[selectedMuelle.value].naves
})

async function loadFullEmbarcaciones() {
  if (
    embarcacionesList.value.length > 0 &&
    requestsList.value.length > 0 &&
    viajesList.value.length > 0 &&
    patronesList.value.length > 0
  ) {
    const vList = embarcacionesList.value
      .map((item) => {
        let request: IRequest | undefined = requestsList.value
          .filter((req) => req.tipo_objeto === RequestObjectType.Embarcacion)
          .filter((req) => req.id_objeto.toString() === item.id.toString())
          .sort((a, b) => new Date(b.fecha_solicitud).getTime() - new Date(a.fecha_solicitud).getTime())[0] // Get latest request
        const asociationsData = asociationsRequests.value
          .filter((req) => req.id_objeto.toString() === item.id.toString())
          .sort((a, b) => new Date(b.fecha_solicitud).getTime() - new Date(a.fecha_solicitud).getTime())
        const preacreditacionesData = preacreditacionRequests.value.sort(
          (a, b) => new Date(b.fecha_solicitud).getTime() - new Date(a.fecha_solicitud).getTime(),
        )

        const activeViajes: Array<ViajePatron> = viajesList.value
          .filter(
            (v) =>
              v.embarcacion_id.toString() === item.id.toString() &&
              new Date(v.fecha_viaje).getTime() >= new Date().getTime() - 1000 * 60 * 60 * 10 &&
              new Date(v.fecha_viaje).getTime() <= new Date().getTime() + 1000 * 60 * 60 * 10,
          )
          .map((viaje) => {
            const requestPatron = patronesRequests.value
              .filter((req) => req.id_objeto.toString() === viaje.id.toString())
              .sort((a, b) => new Date(b.fecha_solicitud).getTime() - new Date(a.fecha_solicitud).getTime())[0] // Get latest request
            const preacreditacionesViaje = preacreditacionesData.filter(
              (p) => p.id_objeto.toString() === viaje.id.toString(),
            )
            if (requestPatron) {
              const patronData = patronesList.value.find(
                (p) => p.id.toString() === requestPatron.id_objeto_2!.toString(),
              )!
              return {
                ...viaje,
                patronData: patronData,
                preacreditaciones: preacreditacionesViaje,
              }
            } else {
              return undefined
            }
          })
          .filter((v) => v !== undefined) as Array<ViajePatron>

        const matriculaValid = item.id_matricula !== 0
        const fotografiaValid = item.id_fotografía !== 0
        const certificadoValid = item.id_certifricado_navegacion !== 0

        const validDocs = fotografiaValid && matriculaValid && certificadoValid
        if (item.tipo_embarcacion != 'auxiliar' && !validDocs) {
          request = undefined
        }
        return {
          ...item,
          requestData: request,
          changesData: [],
          asociationData: asociationsData[0],
          asociationsData: asociationsData,
          cobrosData: [],
          currentCobrosData: [],
          salidasData: [],
          serviciosData: [],
          activeViajes: activeViajes,
        }
      })
      .filter((e) => e.activeViajes.some((v) => v.preacreditaciones.some((p) => p.estado === RequestStatus.APPROVED)))
      .filter(
        (e) =>
          e.activeViajes.length > 0 &&
          e.requestData &&
          (e.requestData.estado === RequestStatus.APPROVED || e.requestData.estado === RequestStatus.REJECTED),
      )
    const separatedViajesList: EmbarcacionViajes[] = []
    vList.forEach((embarcacion) => {
      const viajesMuelleEste = embarcacion.activeViajes.filter((v) => v.muelle == ViajeMuelle.ESTE)
      if (viajesMuelleEste.length > 0) {
        separatedViajesList.push({
          ...embarcacion,
          activeViajes: viajesMuelleEste,
        })
      }
      const viajesMuelleWeste = embarcacion.activeViajes.filter((v) => v.muelle == ViajeMuelle.WESTE)
      if (viajesMuelleWeste.length > 0) {
        separatedViajesList.push({
          ...embarcacion,
          activeViajes: viajesMuelleWeste,
        })
      }
    })
    fullEmbarcacionesList.value = separatedViajesList
  }
}

function onRequestsLoad(requests: Array<IRequest>) {
  requestsList.value = requests.filter((r) => r.tipo_objeto === RequestObjectType.Embarcacion)
  asociationsRequests.value = requests.filter((r) => r.tipo_objeto === RequestObjectType.Asociacion_embarcacion)
  posicionamientoRequests.value = requests.filter((r) => r.tipo_objeto === RequestObjectType.PosicionamientoViaje)
  patronesRequests.value = requests.filter((r) => r.tipo_objeto === RequestObjectType.PatronViaje)
  preacreditacionRequests.value = requests.filter((r) => r.tipo_objeto === RequestObjectType.Preacreditacion)
  loadFullEmbarcaciones()
}

function onViajesLoad(viajes: Array<IViaje>) {
  viajesList.value = viajes
  loadFullEmbarcaciones()
}

function onTripulantesLoad(tripulantes: Array<ITripulante>) {
  patronesList.value = tripulantes
  loadFullEmbarcaciones()
}

function cancelViajes() {
  selectedItemsEmitted.value.forEach((item: EmbarcacionViajes) => {
    item.activeViajes.forEach((viaje) => {
      onViajeCancelado(viaje)
    })
  })
  selectedItemsEmitted.value = []
}

const onViajeCancelado = async (viaje: IViaje) => {
  try {
    viaje.activo = false
    viaje.estado = ViajeEstado.ANULADO
    await dataStorageManager.performOperation(BucketDefaultIDs.VIAJES, 'updateViaje', viaje, [])

    const bitacoraData: IIncidencia = { ...defaultIIncidencia }
    bitacoraData.tipo_incidencia_id = IncidenceType.VIAJE
    bitacoraData.codigo_incidencia = IncidenceCode.CANCELACION
    bitacoraData.descripcion = `El viaje con ID ${viaje.id} ha sido cancelado`
    bitacoraData.id_objeto = viaje.id
    await dataStorageManager.performOperation(BucketDefaultIDs.BITACORA, 'insertIncidencia', bitacoraData)

    notify({
      message: `El viaje ${viaje.id} ha sido cancelado`,
      color: 'success',
    })
  } catch (e) {
    console.error('Error cancelando viaje:', e)
    notify({
      message: `Error cancelando viaje`,
      color: 'danger',
    })
    return
  }
}
</script>

<style scoped>
.body-content {
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

.scrollable-data-list {
  max-height: 520px;
  overflow-y: auto;
}

.table-scroll-wrapper {
  width: 100%;
  overflow-x: auto;
}

.region-revenue-table {
  min-width: 360px;
}
</style>
